import { useState, type ReactElement } from 'react';
import { emberApi } from '../api';
import { trackFailed } from '../../../../../shared-web/track';
import { AvatarPicker } from './AvatarPicker';
import { Modal } from './Modal';
import './AvatarSheet.css';

/**
 * The one place a person changes their picture (§0.9): the shared picker in
 * the shared phone sheet, each tap saving at once. Opened from the /me header
 * avatar and, since 2026-09-13, from the person's own avatar beside their
 * turns in the conversation — a guest included (their visitor row carries the
 * picture; it follows them into the account when they register).
 *
 * The sheet owns the save: `onSaved` hands the page the combo the edge kept.
 * A save that fails leaves the sheet open and the current mark standing — a
 * tap that did nothing, so recorded.
 */
export function AvatarSheet({ avatarKey, onSaved, onClose }: {
  avatarKey: string;
  onSaved: (avatarId: string) => void;
  onClose: () => void;
}): ReactElement {
  const [busy, setBusy] = useState(false);
  const pick = (avatarId: string): void => {
    if (busy) return;
    setBusy(true);
    emberApi.patchMePresence({ avatar_id: avatarId })
      .then((next) => { if (next.avatar_id) onSaved(next.avatar_id); })
      .catch(() => { trackFailed('presence_patch', 'patch_failed', {}); })
      .finally(() => setBusy(false));
  };
  return (
    <Modal onClose={onClose} labelledBy="avatar-sheet-title" sheet="phone" className="avatar-sheet">
      <h2 id="avatar-sheet-title" className="avatar-sheet-title">Your picture</h2>
      <p className="avatar-sheet-help">
        An animal and a background — together they’re how readers see you.
      </p>
      <div className="avatar-sheet-picker">
        <AvatarPicker avatarKey={avatarKey} busy={busy} onPick={pick} />
      </div>
      <div className="avatar-sheet-actions">
        <button type="button" className="ember-btn ember-btn-primary" onClick={onClose} data-autofocus>
          Done
        </button>
      </div>
    </Modal>
  );
}
